import React from 'react';
import { Box, Typography } from '@mui/material';
import { COLOR } from './tokens';

/** Stored run timestamps are UTC but often lack a zone; treat zone-less ISO strings as UTC. */
export function parseRunDate(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number') return new Date(value < 1e12 ? value * 1000 : value);
    let text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(text) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
        text = `${text.replace(' ', 'T')}Z`;
    }
    return new Date(text);
}

export const RUN_DATE_FORMAT = { dateStyle: 'medium', timeStyle: 'short' };

function formatDate(value) {
    const d = parseRunDate(value);
    if (!d) return null;
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, RUN_DATE_FORMAT);
}

/** Summarises a run: game, who played it, score, date and step count. */
export default function RunInfoHeader({ game, player, isAgent = false, score, date, steps, extra = null, sx }) {
    const facts = [
        score != null && score !== '' ? ['Score', String(score)] : null,
        steps != null ? ['Steps', String(steps)] : null,
        formatDate(date) ? ['Date', formatDate(date)] : null,
    ].filter(Boolean);
    return (
        <Box
            data-testid="run-info-header"
            sx={[{
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${COLOR.border}`,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                columnGap: 2,
                rowGap: 1,
            }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" component="p" sx={{ color: COLOR.text, fontWeight: 700, lineHeight: 1.2 }}>
                    {game || 'Replay'}
                </Typography>
                <Typography variant="caption" sx={{ color: COLOR.textMuted, display: 'block' }}>
                    {isAgent ? 'Agent' : 'Player'}: {player || 'Unknown'}
                </Typography>
            </Box>
            {facts.length ? (
                <Box component="dl" sx={{ display: 'flex', gap: 2, m: 0, flexWrap: 'wrap' }}>
                    {facts.map(([label, value]) => (
                        <Box key={label} sx={{ minWidth: 0 }}>
                            <Typography component="dt" variant="caption" sx={{ color: COLOR.textSubtle, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {label}
                            </Typography>
                            <Typography component="dd" variant="body2" sx={{ color: COLOR.text, fontWeight: 600, m: 0 }}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
            {extra ? <Box sx={{ flexBasis: '100%', minWidth: 0 }}>{extra}</Box> : null}
        </Box>
    );
}
